import is from '@sindresorhus/is';
import { type FunctionComponent, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconChevronLeftMedium, IconChevronRightMedium } from '~/ui/core/icons';

import type { CallbackActionInput, QuestionnaireQuestion } from '../../../data-contract/chat';
import { useChatComposerConversation } from '../../chat-composer-contexts';
import { useChatStore } from '../../stores/chat-open-store';
import { QuestionOptionsInput } from './QuestionOptionsInput';
import { currentQuestion, mergedAnswer, QuestionnaireThread, useLocalBlockAnswers } from './questionnaire-flow';

const findCurrentQuestionIndex = (
    questions: readonly QuestionnaireQuestion[],
    local: Readonly<Record<string, string>> | undefined,
): number => questions.findIndex((question) => !is.nonEmptyString(mergedAnswer(question, local)));

const QuestionNavigation: FunctionComponent<{
    readonly currentIndex: number;
    readonly total: number;
    readonly onChange: (index: number) => void;
}> = ({ currentIndex, total, onChange }) => {
    const translate = useTranslate();
    if (total <= 1) return null;

    const canGoBack = currentIndex > 0;
    const canGoForward = currentIndex < total - 1;

    return (
        <div className="flex items-center gap-xs text-xxs font-medium text-black-500">
            <Button
                aria-label={translate('chat.previous-question')}
                className="!h-5 !w-5 !p-0"
                disabled={!canGoBack}
                iconOnly={true}
                onClick={() => onChange(currentIndex - 1)}
                size="sm"
                variant="tertiary"
            >
                <IconChevronLeftMedium size={12} />
            </Button>
            <span className="min-w-8 text-center tabular-nums">
                {currentIndex + 1}/{total}
            </span>
            <Button
                aria-label={translate('chat.next-question')}
                className="!h-5 !w-5 !p-0"
                disabled={!canGoForward}
                iconOnly={true}
                onClick={() => onChange(currentIndex + 1)}
                size="sm"
                variant="tertiary"
            >
                <IconChevronRightMedium size={12} />
            </Button>
        </div>
    );
};

// Asks the block's questions one at a time. Each selection is held in the chat
// store (so the thread can show the question and its answer immediately, then
// reveal the next one); nothing is sent until every question is answered, at
// which point all answers go out in a single callback request.
export const QuestionnaireInputOverride: FunctionComponent<{
    readonly blockId: string;
    readonly eventId: string;
    readonly questions: readonly QuestionnaireQuestion[];
    readonly showCompletedThread?: boolean;
}> = ({ blockId, eventId, questions, showCompletedThread = false }) => {
    const { submitCallbackAnswer } = useChatComposerConversation();
    const local = useLocalBlockAnswers(blockId);
    const setBlockAnswer = useChatStore((state) => state.setBlockAnswer);
    const [activeQuestionIndex, setActiveQuestionIndex] = useState(() => {
        const initialIndex = findCurrentQuestionIndex(questions, local);
        return initialIndex >= 0 ? initialIndex : 0;
    });

    const current = currentQuestion(questions, local);
    if (is.undefined(current)) {
        return showCompletedThread ? <QuestionnaireThread blockId={blockId} questions={questions} /> : null;
    }

    const activeQuestion = questions[activeQuestionIndex] ?? current;
    const selectedAnswer = mergedAnswer(activeQuestion, local);

    const select = (value: string): void => {
        setBlockAnswer(blockId, activeQuestion.actionId, value);

        const answerFor = (question: QuestionnaireQuestion): string =>
            question.actionId === activeQuestion.actionId ? value : (mergedAnswer(question, local) ?? '');

        const allAnswered = questions.every((question) => is.nonEmptyString(answerFor(question)));
        if (!allAnswered) {
            const nextUnansweredIndex = findCurrentQuestionIndex(
                questions,
                Object.fromEntries(questions.map((question) => [question.actionId, answerFor(question)])),
            );
            setActiveQuestionIndex(nextUnansweredIndex);
            return;
        }

        const actions: CallbackActionInput[] = questions.map((question) => ({
            actionId: question.actionId,
            input: answerFor(question),
            type: 'input',
        }));
        submitCallbackAnswer({ actions, blockId, eventId });
    };

    return (
        <QuestionOptionsInput
            headerAction={
                <QuestionNavigation
                    currentIndex={activeQuestionIndex}
                    onChange={setActiveQuestionIndex}
                    total={questions.length}
                />
            }
            question={activeQuestion}
            onSelect={select}
            selectedAnswer={selectedAnswer}
        />
    );
};
